import { BOOKING_TAG_TOOLTIPS } from "@/lib/bookingTagTooltip";
import type { BookingState, MockBooking, PortInfo } from "@/types/booking";

const ORIGINS: PortInfo[] = [
  { locode: "CNNGB", name: "Ningbo" },
  { locode: "CNSHA", name: "Shanghai" },
  { locode: "CNQDG", name: "Qingdao" },
  { locode: "CNYTN", name: "Yantian" },
  { locode: "KRPUS", name: "Busan" },
  { locode: "VNSGN", name: "Ho Chi Minh" },
];

const DESTINATIONS: PortInfo[] = [
  { locode: "ILASH", name: "Ashdod" },
  { locode: "ILHFA", name: "Haifa" },
];

const CARRIERS = ["MSK", "MSC", "CMA", "ZIM", "HPL", "ONE"] as const;
const AGENTS = ["FCS Gen", "MedLink", "BlueWave", "HarborLine"];
const SHIPPERS = [
  "Sterling Textiles",
  "Nova Electronics",
  "Atlas Furniture",
  "GreenFresh Produce",
  "Pacific Machinery",
];
const COMMODITIES = [
  "Cotton, raw",
  "Electronics",
  "Furniture",
  "Produce",
  "Machinery",
  "Textiles",
];
const EQUIPMENT = [
  "1 × 20' Dry",
  "2 × 40' HC",
  "1 × 40' Dry",
  "1 × 20' Reefer",
  "3 × 40' Dry",
  "1 × 40' HC",
];

const STATES: BookingState[] = [
  "pending",
  "pending_book",
  "book",
  "book_restricted",
  "rolled",
  "stuffed",
  "shipped",
  "delivered",
  "rebooked",
];

function addDays(base: Date, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function isoWeek(date: Date): string {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

const today = new Date();

function buildBooking(index: number): MockBooking {
  const origin = ORIGINS[index % ORIGINS.length];
  const destination = DESTINATIONS[index % DESTINATIONS.length];
  const carrier = CARRIERS[index % CARRIERS.length];
  const state = STATES[index % STATES.length];
  const etdOffset = 2 + (index % 14);
  const transitDays = 18 + (index % 8);
  const departure = addDays(today, etdOffset);
  const arrival = addDays(today, etdOffset + transitDays);
  const openDate = addDays(today, -(5 + (index % 20)));
  const stuffing = addDays(today, etdOffset - 3);
  const agentCutoff = index % 3 !== 0 ? addDays(today, etdOffset - 2) : undefined;
  const scheduleCutoff = addDays(today, etdOffset - 1);
  const pricing = 1800 + index * 137;
  const hasOpportunity = index % 4 === 0;
  const saving = hasOpportunity ? 280 + (index % 6) * 110 : undefined;

  const tags: string[] = [];
  if (hasOpportunity) tags.push("Opportunity");
  if (state === "rolled") tags.push("Rolled");
  if (index % 7 === 0) tags.push("DG");
  if (index % 9 === 0) tags.push("Reefer");

  // Rebooked bookings carry a chain-net rebook summary. Alternate between a
  // realized saving (green) and a cost increase (red) so local dev exercises
  // both states of the Saving column and the REBOOKED tag.
  let rebookInfo: MockBooking["rebook_info"];
  if (state === "rebooked") {
    tags.push("REBOOKED");
    const savedMoney = index % 2 === 0;
    const before = pricing;
    const delta = savedMoney ? 420 + (index % 5) * 90 : -(310 + (index % 4) * 70);
    const after = before - delta;
    const altCarrier = CARRIERS[(index + 2) % CARRIERS.length];
    rebookInfo = {
      rebooked_from_source_id: String(8841000 + index * 173 - 4),
      before_carrier: carrier,
      before_contract_ref: `${carrier}-2025-${String(100 + (index % 40)).padStart(3, "0")}`,
      after_carrier: altCarrier,
      after_contract_ref: `${altCarrier}-2026-${String(200 + (index % 40)).padStart(3, "0")}`,
      before_pricing_total: before,
      after_pricing_total: after,
      before_currency: "USD",
      after_currency: "USD",
      pricing_delta: delta,
      delta_percentage: before ? (delta / before) * 100 : null,
      chain_step_count: index % 3 === 0 ? 2 : 1,
      detected_at: addDays(today, etdOffset - 12),
    };
  }

  return {
    id: `bk-${index + 1}`,
    source_id: String(8841000 + index * 173),
    state,
    tags,
    flags:
      tags.length > 0
        ? {
            "": tags.map((tag) => ({
              tag,
              tooltip:
                tag === "DG" ? "Class 9 · UN3091" : BOOKING_TAG_TOOLTIPS[tag],
            })),
          }
        : undefined,
    carrier,
    contract_ref: `${carrier}-2025-${String(100 + (index % 40)).padStart(3, "0")}`,
    booking_ref:
      index % 3 === 0
        ? `${carrier}${String(8821000 + index * 41)}`
        : undefined,
    origin_port: origin,
    destination_port: destination,
    departure_date: departure,
    arrival_date: arrival,
    stuffing_date: stuffing,
    stuffing_date_source: index % 5 === 0 ? "carrier_tracking" : "update",
    cutoff_date: agentCutoff,
    effective_cutoff: agentCutoff ?? scheduleCutoff,
    cutoff_source: agentCutoff ? "update" : "schedule",
    effective_pcd: index % 4 === 0 ? departure : addDays(today, etdOffset - 5),
    pcd_is_etd_fallback: index % 4 === 0,
    vessel: index % 3 === 0 ? `MV ${carrier} STAR ${index + 10}` : undefined,
    equipment_summary: EQUIPMENT[index % EQUIPMENT.length],
    total_teu: 1 + (index % 4),
    total_containers: 1 + (index % 3),
    load_type: index % 11 === 0 ? "lcl" : "fcl",
    weight: 18000 + index * 250,
    containers: [
      {
        equipment_type: index % 9 === 0 ? "Reefer" : index % 2 === 0 ? "HC" : "DV",
        equipment_size: index % 3 === 0 ? "20" : "40",
        quantity: 1 + (index % 3),
        weight_kg: 18000 + index * 250,
        is_dg: index % 7 === 0,
        cargo_weight_kg: 18000 + index * 250,
        tare_weight_kg: index % 3 === 0 ? 2200 : 3800,
        gross_weight_kg: 18000 + index * 250 + (index % 3 === 0 ? 2200 : 3800),
      },
    ],
    shipper: SHIPPERS[index % SHIPPERS.length],
    consignee: index % 2 === 0 ? "IL Consignee Ltd" : "Haifa Imports Co",
    agent: AGENTS[index % AGENTS.length],
    week: isoWeek(new Date(departure)),
    commodity: COMMODITIES[index % COMMODITIES.length],
    open_date: openDate,
    sales_person: index % 3 === 0 ? "Amit Sterling" : "Ops Team",
    pricing_total: pricing,
    pricing_currency: "USD",
    best_saving: saving,
    rebook_info: rebookInfo,
    bl_group_id: index % 5 === 0 ? `BLG-${100 + index}` : undefined,
  };
}

export const mockBookings: MockBooking[] = Array.from({ length: 28 }, (_, i) =>
  buildBooking(i),
);

export function getMockBookingBySourceId(sourceId: string): MockBooking | undefined {
  const decoded = decodeURIComponent(sourceId);
  return mockBookings.find((b) => b.source_id === decoded);
}
